// Provenance bar above the report frame (design §2.6, §7): who made this
// version, in which run, which version, when. It sits outside the iframe in
// Goblic's own chrome, so a report can never pass for a Goblic screen.

import { Link } from '@tanstack/react-router'
import { formatDateTime, formatRelative, shortId } from '../../lib/format'
import {
  isAgentAuthored,
  type ReportProvenance as ReportProvenanceData,
} from './report-types'

export interface ReportProvenanceProps {
  provenance: ReportProvenanceData
  /** Version being shown. */
  version: number
  /** Latest version of the report, to mark an older one being shown. */
  currentVersion?: number
}

export function ReportProvenance({
  provenance,
  version,
  currentVersion,
}: ReportProvenanceProps) {
  const byAgent = isAgentAuthored(provenance)
  const older = currentVersion != null && version < currentVersion

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-control border border-line bg-sunken px-3 py-2 text-sm text-ink-2">
      <span
        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
          byAgent ? 'bg-signal-soft text-ink' : 'bg-info-soft text-info'
        }`}
      >
        {byAgent ? 'Report made by an agent' : 'Report written by a person'}
      </span>
      <span className="min-w-0">
        {byAgent ? (
          <span className="font-medium text-ink">
            {provenance.agentName ?? 'Agent'}
          </span>
        ) : (
          <span className="font-medium text-ink">
            {provenance.userName ?? 'Someone'}
          </span>
        )}
        {provenance.runId ? (
          <>
            {' · '}
            <Link
              to="/runs/$runId"
              params={{ runId: provenance.runId }}
              className="link"
            >
              run {shortId(provenance.runId)}
            </Link>
          </>
        ) : null}
        {' · '}
        <span>version {version}</span>
        {older ? (
          <span className="ml-1.5 rounded-full bg-sunken px-1.5 text-xs font-medium text-ink-3 ring-1 ring-line">
            older version
          </span>
        ) : null}
        {' · '}
        <span title={formatDateTime(provenance.createdAt)}>
          {formatRelative(provenance.createdAt)}
        </span>
      </span>
    </div>
  )
}
